import type { RecordFormFieldConfig } from "./RecordFormWidget";

/**
 * Derive the default field list of a `record_form` dashboard widget from the
 * create schema of the record type it creates.
 *
 * A record_form widget whose `fields` is empty shows the same inputs as the
 * create-record screen of the main Create menu: the object template's shown
 * (non-hidden) attributes in schema order, preceded by a Name field. Attributes
 * whose element is not `form-optional-*` and that carry no default are marked
 * required, matching the create screen's required cue.
 *
 * The widget only renders string inputs (text / text area / select), so
 * attributes of other shapes (record references, booleans, numbers, JSON) are
 * left out — the create screen remains the place to set those.
 * (operator-task-fe-record-form-widget-seed-required-fields-from-create-schema)
 */

const TEXT_ELEMENTS = new Set(["form-string", "form-optional-string"]);
const TEXTAREA_ELEMENTS = new Set([
    "form-text",
    "form-optional-text",
    "form-markdown",
    "form-optional-markdown",
]);
const DROPDOWN_ELEMENTS = new Set(["form-dropdown", "form-optional-dropdown"]);

const isOptionalElement = (element: unknown) =>
    typeof element === "string" && element.startsWith("form-optional-");

/** The string choices of a dropdown attribute (oneOf/anyOf of form-const-string), or null. */
const dropdownOptions = (property: any): { label: string; value: string }[] | null => {
    const branches: any[] = Array.isArray(property?.oneOf)
        ? property.oneOf
        : Array.isArray(property?.anyOf)
          ? property.anyOf
          : [];
    const options = branches
        .filter((branch) => typeof branch?.const === "string")
        .map((branch) => ({ label: String(branch.label ?? branch.const), value: branch.const as string }));
    return options.length > 0 ? options : null;
};

const hiddenList = (schema: any): string[] => (Array.isArray(schema?.hidden) ? schema.hidden : []);

/** Property names in display order: `order` first, then the rest by name. */
const orderedPropertyNames = (schema: any): string[] => {
    const properties = Object.keys(schema?.properties ?? {});
    const order: string[] = Array.isArray(schema?.order) ? schema.order : [];
    const inOrder = order.filter((name) => properties.includes(name));
    const rest = properties.filter((name) => !inOrder.includes(name)).sort();
    return [...inOrder, ...rest];
};

const attributeField = (name: string, property: any): RecordFormFieldConfig | null => {
    const element = property?.element;
    // A required string attribute carries `default: ""` in the schema, which is
    // not a usable default; a dropdown names its default by `defaultIndex`.
    const options = DROPDOWN_ELEMENTS.has(element) ? dropdownOptions(property) : null;
    let defaultValue: string | undefined;
    if (typeof property?.default === "string" && property.default !== "") {
        defaultValue = property.default;
    } else if (options && Number.isInteger(property?.defaultIndex)) {
        defaultValue = options[property.defaultIndex]?.value;
    }
    const required = !isOptionalElement(element) && defaultValue === undefined;
    const field: RecordFormFieldConfig = {
        path: `value.${name}`,
        label: typeof property?.label === "string" && property.label ? property.label : name,
    };
    if (DROPDOWN_ELEMENTS.has(element)) {
        if (!options) {
            return null;
        }
        field.input = "select";
        field.options = options;
    } else if (TEXTAREA_ELEMENTS.has(element)) {
        field.input = "textarea";
    } else if (TEXT_ELEMENTS.has(element)) {
        field.input = "text";
    } else {
        return null;
    }
    if (required) {
        field.required = true;
    }
    if (defaultValue !== undefined) {
        field.default = defaultValue;
    }
    return field;
};

export interface DeriveRecordFormFieldsOptions {
    /** Include a top-level Name input first (the create screen's Name field). */
    includeName?: boolean;
}

/**
 * Fields for a record_form widget, derived from an object template's `schema`
 * (pass `null` for record types without one — only the Name field is derived).
 */
export const deriveRecordFormFields = (
    objectTemplateSchema: unknown,
    { includeName = true }: DeriveRecordFormFieldsOptions = {}
): RecordFormFieldConfig[] => {
    const fields: RecordFormFieldConfig[] = [];
    if (includeName) {
        fields.push({ path: "name", label: "Name", input: "text" });
    }
    const schema: any = objectTemplateSchema;
    if (!schema || typeof schema !== "object" || !schema.properties) {
        return fields;
    }
    const hidden = hiddenList(schema);
    orderedPropertyNames(schema)
        .filter((name) => !hidden.includes(name))
        .forEach((name) => {
            const field = attributeField(name, schema.properties[name]);
            if (field) {
                fields.push(field);
            }
        });
    return fields;
};
